import loadingBars from '@fd-assets/core-styles/client/loadingBars';

import errorNotification from '@grins/fd-modules/bonz/modules/errorNotification';
import utils from '@grins/fd-modules/bonz/modules/utils';

import bonzConsts from '@grins/fd-modules/bonz/shared/constants';

import Fandango_constants_HTTPStatusCodes from '@grins/fd-modules/Fandango/constants';

import inputValidator from '@grins/fd-modules/shared/inputValidator';

import {
  Fandango_adobeLaunch as Fandango_adobeLaunch_track,
  Fandango_xhr as xhr,
  fd_url_manager as links,
  focusableInput_form as focusableForm
} from '@grins/fd-modules/splits/onering-global';

type SignInFields = HTMLFormControlsCollection & {
  email: HTMLInputElement;
  password: HTMLInputElement;
};

const { track } = Fandango_adobeLaunch_track;
const { HTTPStatusCodes } = Fandango_constants_HTTPStatusCodes;

const Selectors = {
  FORM: '#signin-form',
  SAVE_BTN: '#sign-in-submit-btn',
};

const Styles = {
  HIDE: 'hide',
  LOADING_FULL: 'btn--loading-wrap',
};

const Msgs = {
  GENERIC_ERROR: 'Something went wrong on our end.  Please try again later.',
  WRONG_PASSWORD: 'The account information you have entered does not match our records. Please try again.',
};

function setSaving(isSaving: boolean) {
  const saveBtn = document.querySelector(Selectors.SAVE_BTN);

  if (isSaving) {
    loadingBars.start(saveBtn, { className: Styles.LOADING_FULL });
  } else {
    loadingBars.stop(saveBtn);
  }
}

function validateFields(fields: SignInFields) {
  const { ValidationTypes, validate } = inputValidator;
  const { email, password } = fields;

  const validationRules = [{
    input: email,
    displayName: 'Email',
    isRequired: true,
    type: ValidationTypes.EMAIL,
  }, {
    input: password,
    displayName: 'Password',
    isRequired: true,
    type: ValidationTypes.CUSTOM,
    failRules: [{ regex: '^.{1,7}$', msg: 'Your current password is too short' }]
  }];

  const { errors, hasErrors } = validate(validationRules);
  return { errors, hasErrors };
}

function trackError(msg: string) {
  track({ event: bonzConsts.OmnitureEvents.SIGNIN_ERROR, errorMessage: msg });
}

function onFail(err?: { status?: number }) {
  setSaving(false);
  const status = err && err.status;

  const msg = status === HTTPStatusCodes.FORBIDDEN || status === HTTPStatusCodes.UNAUTHORIZED
    ? Msgs.WRONG_PASSWORD
    : Msgs.GENERIC_ERROR;

  trackError(msg);
  errorNotification.show(msg);
}

function onDone(response: { success: boolean }, email: string, password: string) {
  setSaving(false);

  if (!response || !response.success) {
    return trackError(Msgs.GENERIC_ERROR);
  }

  track(bonzConsts.OmnitureEvents.SIGNIN_COMPLETE);
  utils.addFavorite();
  utils.saveAuthenticationToApp(email, password, 'signin');
  setTimeout(() => window.location.href = utils.getDestinationURI(), 250);
}

async function onSubmit(this: HTMLFormElement, e: SubmitEvent) {
  e.preventDefault();
  errorNotification.hide();
  setSaving(true);
  const fields = this.elements as SignInFields;
  const { errors, hasErrors } = validateFields(fields);

  if (hasErrors) {
    setSaving(false);
    return focusableForm.toggleErrors(errors ?? [], true);
  }

  const email: string = fields.email.value;
  const password: string = fields.password.value;
  const data = { email, password };

  xhr.ajax({ url: links.snapiBonzSignIn(), method: 'POST', data })
    ?.done((response: { success: boolean }) => onDone(response, email, password))
    .fail(onFail);
}

function init() {
  const form = document.querySelector<HTMLFormElement>(Selectors.FORM);
  form?.addEventListener('submit', onSubmit);
  focusableForm.attach(form);
}

export default {
  init
};
